import React, { useEffect, useRef, useState } from "react";

// Live "evidence being captured" ticker — a self-contained demo of the System of
// Proof in motion. Synthetic artefacts stream in on an interval, tagged by the
// five EGMM pillars, reinforcing "evidence, generated as the work is done".
// Honours prefers-reduced-motion (renders a static, full feed, no streaming).
const PILLARS = {
  Provenance: "#C9B8FB",
  Challenge: "#A179FA",
  Continuity: "#8B6FE8",
  Control: "#7737F6",
  Disclosure: "#D8CBFF",
};

const FEED = [
  ["Provenance", "Decision D-142 linked to source diagnostic + owner"],
  ["Challenge", "Assumption A-07 challenged — RAG moved amber → green"],
  ["Continuity", "Milestone M-3 evidence carried into board pack v4"],
  ["Control", "Segregation-of-duties check passed on release R-19"],
  ["Disclosure", "Regulator pack assembled — 24 controls, trail attached"],
  ["Provenance", "Benefit B-11 traced to value stream Order-to-Cash"],
  ["Challenge", "Confidence band re-rated: Moderate → High (evidence tier ↑)"],
  ["Continuity", "Day-log signed off; margin recomputed on engagement E-8"],
  ["Control", "Access review logged for privileged utility on DSS05"],
  ["Disclosure", "SoA export stamped: reviewed & approved, 93 controls"],
];

const stamp = () => {
  const d = new Date();
  return d.toTimeString().slice(0, 8);
};

export const EvidenceTicker = () => {
  const [rows, setRows] = useState([]);
  const idx = useRef(0);

  useEffect(() => {
    const still = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still) {
      setRows(FEED.slice(0, 5).map(([p, t], i) => ({ id: i, p, t, ts: stamp() })));
      return;
    }
    // seed with two, then stream
    setRows(FEED.slice(0, 2).map(([p, t], i) => ({ id: i, p, t, ts: stamp() })));
    idx.current = 2;
    const iv = setInterval(() => {
      const [p, t] = FEED[idx.current % FEED.length];
      idx.current += 1;
      setRows((prev) => [{ id: idx.current, p, t, ts: stamp() }, ...prev].slice(0, 5));
    }, 2600);
    return () => clearInterval(iv);
  }, []);

  return (
    <div data-testid="sop-evidence-ticker"
         className="mt-10 max-w-2xl rounded-xl border border-white/12 bg-white/[0.03] backdrop-blur-sm p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9B8FB] opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C9B8FB]" />
        </span>
        <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-400">Evidence being captured — live</span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} data-testid="sop-evidence-row"
               className="flex items-start gap-3 text-[13px] ek-ticker-row">
            <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: PILLARS[r.p] }} />
            <span className="font-mono text-[11px] text-zinc-500 mt-0.5 shrink-0 tabular-nums">{r.ts}</span>
            <span className="font-semibold shrink-0" style={{ color: PILLARS[r.p] }}>{r.p}</span>
            <span className="text-zinc-300 font-light leading-snug">{r.t}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EvidenceTicker;
